import { joinRoom, selfId } from 'trystero/mqtt';
import Debug from '../utils/Debug.js';

/**
 * Core P2P networking orchestrator.
 * Manages Trystero room connections, data channels, and peer lifecycle.
 */
export default class NetworkManager {
  constructor() {
    this.room = null;
    this.peerId = selfId; // Trystero's own stable ID — consistent across all peers
    this.peers = new Map(); // trystero peerId -> {}
    this.roomId = null;

    // Data channel senders (set when room is joined)
    this._sendOp = null;
    this._sendPlayer = null;
    this._sendSync = null;
    this._sendNpcState = null;
    this._sendAction = null;
    this._sendChat = null;

    // In-flight guards: prevent concurrent sends from accumulating when the
    // data channel is congested. Only one broadcast per channel type at a time;
    // intermediate updates are dropped (next broadcast sends latest state).
    this._playerSendInFlight = false;
    this._npcSendInFlight = false;

    // Chunked world state transfer: accumulates incoming chunks until complete
    this._worldTransferBuffers = new Map(); // transferId -> { chunks[], received, total, compressed, timeout }
    // Dedup: only send one request-world-raw per session
    this._rawWorldStateRequested = false;

    // Callbacks — set by WorldSync, PlayerSync, NPCSync, etc.
    this.onPeerJoin = null;
    this.onPeerLeave = null;
    this.onOperation = null;
    this.onPlayerUpdate = null;
    this.onNPCStates = null;
    this.onAction = null;
    this.onSyncRequest = null;
    this.onSyncRequestRaw = null;
    this.onWorldState = null;

    // Diagnostic callback — set by main.js during room join for visible logging
    this._onDiagnostic = null;

    // Dungeon seed callback — set by DungeonInstanceManager for late-join seed transfer
    this.onDungeonSeed = null;
    this.onChat = null;
  }

  /**
   * Join a room by ID. Creates WebRTC connections to all peers in that room.
   * @param {string} roomId - Room identifier
   */
  joinRoom(roomId) {
    if (this.room) {
      this.leave();
    }

    this.roomId = roomId;

    this.room = joinRoom(
      { appId: 'trixel-me' },
      roomId
    );

    // Create typed data channels
    const [sendOp, getOp] = this.room.makeAction('op');
    const [sendPlayer, getPlayer] = this.room.makeAction('player');
    const [sendSync, getSync] = this.room.makeAction('sync');
    const [sendNpcState, getNpcState] = this.room.makeAction('npcState');
    const [sendAction, getAction] = this.room.makeAction('action');
    const [sendChat, getChat] = this.room.makeAction('chat');

    this._sendOp = sendOp;
    this._sendPlayer = sendPlayer;
    this._sendSync = sendSync;
    this._sendNpcState = sendNpcState;
    this._sendAction = sendAction;
    this._sendChat = sendChat;

    // Wire receive handlers
    getOp((data, peerId) => this.onOperation?.(data, peerId));
    getPlayer((data, peerId) => this.onPlayerUpdate?.(data, peerId));
    getNpcState((data, peerId) => this.onNPCStates?.(data, peerId));
    getAction((data, peerId) => this.onAction?.(data, peerId));
    getSync((data, peerId) => this._handleSync(data, peerId));
    getChat((data, peerId) => this.onChat?.(data, peerId));

    // Peer lifecycle
    this.room.onPeerJoin(peerId => {
      this.peers.set(peerId, {});
      this.onPeerJoin?.(peerId);
    });
    this.room.onPeerLeave(peerId => {
      this.peers.delete(peerId);
      this.onPeerLeave?.(peerId);
    });
  }

  /**
   * Broadcast a world operation to all connected peers.
   * @param {Object} op - Operation data
   */
  broadcastOperation(op) {
    this._sendOp?.(op)?.catch?.(() => {});
  }

  /**
   * Broadcast local player state to all connected peers.
   * @param {Object} state - Player position/animation state
   * @param {boolean} force - Bypass in-flight guard (for peer discovery broadcasts
   *   that MUST go through even when the channel is congested)
   */
  broadcastPlayerState(state, force = false) {
    if (!force && this._playerSendInFlight) return; // Drop — next tick sends latest state
    const p = this._sendPlayer?.(state);
    if (p?.then) {
      this._playerSendInFlight = true;
      // Safety valve: if Trystero's send promise hangs (peer disconnects during
      // congestion), auto-reset after 5s so broadcasts resume.
      const timeout = setTimeout(() => { this._playerSendInFlight = false; }, 5000);
      p.then(
        () => { clearTimeout(timeout); this._playerSendInFlight = false; },
        () => { clearTimeout(timeout); this._playerSendInFlight = false; }
      );
    }
  }

  /**
   * Broadcast NPC states to all connected peers (authority peer only).
   * @param {Array} states - Array of { id, x, y, z, anim, dir, isMoving }
   */
  broadcastNPCStates(states) {
    if (this._npcSendInFlight) return;
    const p = this._sendNpcState?.(states);
    if (p?.then) {
      this._npcSendInFlight = true;
      const timeout = setTimeout(() => { this._npcSendInFlight = false; }, 5000);
      p.then(
        () => { clearTimeout(timeout); this._npcSendInFlight = false; },
        () => { clearTimeout(timeout); this._npcSendInFlight = false; }
      );
    }
  }

  /**
   * Broadcast an instant action event (jump, attack, fireball) to all peers.
   * @param {Object} data - Action data ({ type, animName, ... })
   */
  broadcastAction(data) {
    this._sendAction?.(data)?.catch?.(() => {});
  }

  /**
   * Broadcast a chat message to all connected peers.
   * @param {Object} msg - { name, msg, ts }
   */
  broadcastChat(msg) {
    this._sendChat?.(msg)?.catch?.(() => {});
  }

  /**
   * Broadcast dungeon seed/config to all peers (for seed-based generation).
   * @param {Object} config - { theme, levelCount, seed }
   */
  broadcastDungeonSeed(config) {
    this._sendSync?.({ type: 'dungeon-seed', config })?.catch?.(() => {});
  }

  /**
   * Send dungeon seed/config to a specific peer (for late joiners).
   * @param {string} peerId
   * @param {Object} config - { theme, levelCount, seed }
   */
  sendDungeonSeed(peerId, config) {
    this._sendSync?.({ type: 'dungeon-seed', config }, peerId)?.catch?.(() => {});
  }

  /**
   * Send full world state to a specific peer (for initial sync).
   * Compresses with gzip and sends as small chunks (~16KB each) to work
   * reliably on all platforms — Android Chrome's WebRTC data channels
   * silently drop large messages even well below the theoretical 256KB SCTP limit.
   * @param {string} peerId - Target peer
   * @param {Object} json - Full world JSON from world.toJSON()
   * @param {Object} options
   * @param {boolean} options.compress - Whether to try gzip compression (default: true)
   */
  async sendWorldState(peerId, json, { compress = true } = {}) {
    const CHUNK_SIZE = 16000; // ~16KB per chunk — safe on all platforms
    // Accept pre-serialized string to avoid redundant JSON.stringify() calls
    // when the same world state is sent to multiple peers or retried.
    const jsonStr = typeof json === 'string' ? json : JSON.stringify(json);
    let payload;
    let compressed = false;

    // Try to compress (gzip typically reduces 300KB JSON to ~30-50KB)
    if (compress) {
      try {
        if (typeof CompressionStream !== 'undefined') {
          const compressedBytes = await this._compress(jsonStr);
          payload = this._uint8ToBase64(compressedBytes);
          compressed = true;
          Debug.log('NetworkManager', `World state compressed: ${jsonStr.length} → ${payload.length} bytes (${Math.round(payload.length / jsonStr.length * 100)}%)`);
        }
      } catch (e) {
        Debug.warn('NetworkManager', 'Compression failed, sending uncompressed:', e);
      }
    }

    if (!payload) {
      payload = jsonStr;
    }

    // Split into chunks and send each as a separate small message
    const totalChunks = Math.ceil(payload.length / CHUNK_SIZE);
    const transferId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    Debug.log('NetworkManager', `Sending world state: ${totalChunks} chunks, ${compressed ? 'compressed' : 'raw'}, transfer=${transferId}`);

    for (let i = 0; i < totalChunks; i++) {
      const chunk = payload.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      this._sendSync?.({
        type: 'world-chunk',
        id: transferId,
        idx: i,
        total: totalChunks,
        gz: compressed,
        data: chunk
      }, peerId)?.catch?.(() => {});
    }
  }

  /**
   * Request world state from connected peers.
   */
  requestWorldState() {
    this._sendSync?.({ type: 'request-world' })?.catch?.(() => {});
  }

  /**
   * Request world state WITHOUT compression from connected peers.
   * Used as fallback when decompression fails on this device.
   */
  requestWorldStateRaw() {
    Debug.log('NetworkManager', 'Requesting uncompressed world state (decompression fallback)');
    this._sendSync?.({ type: 'request-world-raw' })?.catch?.(() => {});
  }

  /**
   * Leave the current room and disconnect from all peers.
   */
  leave() {
    this.room?.leave();
    this.room = null;
    this.roomId = null;
    this.peers.clear();
    this._sendOp = null;
    this._sendPlayer = null;
    this._sendSync = null;
    this._sendNpcState = null;
    this._sendAction = null;
    this._sendChat = null;
    this._playerSendInFlight = false;
    this._npcSendInFlight = false;
    this._rawWorldStateRequested = false;
    // Clean up pending chunk transfers
    for (const buffer of this._worldTransferBuffers.values()) {
      clearTimeout(buffer.timeout);
    }
    this._worldTransferBuffers.clear();
  }

  get isConnected() {
    return this.room !== null;
  }

  get peerCount() {
    return this.peers.size;
  }

  /**
   * Check if this peer is the authority (deterministic).
   * Uses lexicographic comparison of Trystero peer IDs — all peers
   * see the same IDs, so they agree on who is authority.
   */
  isOldestPeer() {
    for (const peerId of this.peers.keys()) {
      if (peerId < selfId) return false;
    }
    return true;
  }

  _handleSync(data, peerId) {
    if (data.type === 'request-world') {
      this._onDiagnostic?.(`Sync: request-world from ${peerId.slice(0,6)}`);
      this.onSyncRequest?.(peerId);
    } else if (data.type === 'request-world-raw') {
      // Peer's decompression failed — resend without compression
      this.onSyncRequestRaw?.(peerId);
    } else if (data.type === 'world-chunk') {
      this._handleWorldChunk(data);
    } else if (data.type === 'world-state-gz') {
      // Legacy: single compressed message (backward compat)
      this._receiveCompressedWorldState(data.data);
    } else if (data.type === 'world-state') {
      // Legacy: single uncompressed message
      this.onWorldState?.(data.json);
    } else if (data.type === 'dungeon-seed') {
      // Dungeon seed transfer for late joiners
      this.onDungeonSeed?.(data.config, peerId);
    }
  }

  /**
   * Accumulate incoming world state chunks. When all chunks for a transfer
   * arrive, reassemble and deliver the complete world state.
   */
  _handleWorldChunk(data) {
    const { id, idx, total, gz, data: chunk } = data;

    let buffer = this._worldTransferBuffers.get(id);
    if (!buffer) {
      const msg = `Receiving: ${total} chunks, ${gz ? 'gzip' : 'raw'} (transfer ${id.slice(0,6)})`;
      Debug.log('NetworkManager', msg);
      this._onDiagnostic?.(msg);
      buffer = {
        chunks: new Array(total),
        received: 0,
        total,
        compressed: gz,
        timeout: setTimeout(() => {
          const received = this._worldTransferBuffers.get(id)?.received || 0;
          const msg = `Transfer timed out (${received}/${total} chunks)`;
          Debug.warn('NetworkManager', msg);
          this._onDiagnostic?.(msg);
          this._worldTransferBuffers.delete(id);
        }, 30000)
      };
      this._worldTransferBuffers.set(id, buffer);
    }

    // Deduplicate (same chunk index from a retry)
    if (!buffer.chunks[idx]) {
      buffer.chunks[idx] = chunk;
      buffer.received++;
    }

    // Log progress every few chunks
    if (buffer.received === 1 || buffer.received === buffer.total || buffer.received % 5 === 0) {
      this._onDiagnostic?.(`Chunks: ${buffer.received}/${buffer.total}`);
    }

    // All chunks received — reassemble
    if (buffer.received === buffer.total) {
      Debug.log('NetworkManager', `All ${buffer.total} chunks received for transfer ${id}, reassembling...`);
      this._onDiagnostic?.('All chunks received, reassembling...');
      clearTimeout(buffer.timeout);
      this._worldTransferBuffers.delete(id);
      this._reassembleWorldState(buffer);
    }
  }

  /**
   * Reassemble a complete world state from collected chunks.
   * If decompression fails (e.g., DecompressionStream not available on Android),
   * requests an uncompressed retransmission from the sender.
   */
  async _reassembleWorldState(buffer) {
    try {
      const payload = buffer.chunks.join('');
      this._onDiagnostic?.(`Payload: ${(payload.length / 1024).toFixed(0)}KB, ${buffer.compressed ? 'compressed' : 'raw'}`);
      let json;
      if (buffer.compressed) {
        try {
          if (typeof DecompressionStream === 'undefined') {
            throw new Error('DecompressionStream API not available');
          }
          this._onDiagnostic?.('Decompressing (gzip)...');
          const bytes = this._base64ToUint8(payload);
          const jsonStr = await this._decompress(bytes);
          json = JSON.parse(jsonStr);
          this._onDiagnostic?.(`Decompressed OK: ${(jsonStr.length / 1024).toFixed(0)}KB`);
          Debug.log('NetworkManager', `World state decompressed successfully (${payload.length} → ${jsonStr.length} bytes)`);
        } catch (decompErr) {
          const msg = `Decompression FAILED: ${decompErr.message}`;
          Debug.error('NetworkManager', msg);
          this._onDiagnostic?.(msg);
          // Request uncompressed retransmission — _hasReceivedWorldState is still false
          // (onWorldState was never called), so the retransmission will be accepted.
          // Dedup: only send one raw request (host's staggered retries would each
          // fail decompression, but we only need one raw response).
          if (!this._rawWorldStateRequested) {
            this._rawWorldStateRequested = true;
            this._onDiagnostic?.('Requesting raw (uncompressed) retransmission...');
            Debug.log('NetworkManager', 'Requesting uncompressed world state (decompression fallback)');
            this.requestWorldStateRaw();
          } else {
            this._onDiagnostic?.('Raw already requested, waiting...');
          }
          return;
        }
      } else {
        json = JSON.parse(payload);
        this._onDiagnostic?.(`Parsed OK: ${(payload.length / 1024).toFixed(0)}KB raw`);
        Debug.log('NetworkManager', `World state parsed (uncompressed, ${payload.length} bytes)`);
      }
      this._onDiagnostic?.(`Delivering: ${json.cuboids?.length || 0} cuboids, ${json.npcs?.length || 0} NPCs`);
      this.onWorldState?.(json);
    } catch (e) {
      const msg = `Reassembly FAILED: ${e.message}`;
      Debug.error('NetworkManager', msg);
      this._onDiagnostic?.(msg);
    }
  }

  // Legacy: handle single compressed message (backward compat)
  async _receiveCompressedWorldState(base64Data) {
    try {
      const bytes = this._base64ToUint8(base64Data);
      const jsonStr = await this._decompress(bytes);
      const json = JSON.parse(jsonStr);
      this.onWorldState?.(json);
    } catch (e) {
      Debug.warn('NetworkManager', 'Failed to decompress world state:', e);
    }
  }

  // --- Compression helpers (gzip via native CompressionStream) ---

  async _compress(str) {
    const blob = new Blob([str]);
    const stream = blob.stream().pipeThrough(new CompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }

  async _decompress(bytes) {
    const blob = new Blob([bytes]);
    const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'));
    return new Response(stream).text();
  }

  _uint8ToBase64(bytes) {
    const CHUNK = 0x8000; // 32KB chunks to avoid call stack limits
    const parts = [];
    for (let i = 0; i < bytes.length; i += CHUNK) {
      parts.push(String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK)));
    }
    return btoa(parts.join(''));
  }

  _base64ToUint8(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
